import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { toContextArrayOrEmpty } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { ViewManager, ListDisplayRowElement } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bhub-list-display-cell',
  standalone: false,
  templateUrl: './list-display-cell.component.html',
  styleUrl: './list-display-cell.component.scss'
})
export class ListDisplayCellComponent extends BaseCellComponent implements OnInit, OnDestroy {
  @Input() viewManager: ViewManager | null = null;

  listItems: any[] = [];
  private destroy$ = new Subject<void>();

  constructor() {
    super();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected initializeCell(): void {
    this.loadListData();
  }

  protected onCellOrDataChange(): void {
    this.loadListData();
  }

  protected onCellUpdate(): void {
    this.loadListData();
  }

  protected onDataSet(data: any): void {
    this.loadListData();
  }

  reloadData(): void {
    this.loadListData();
  }

  private loadListData(): void {
    const contextVariable = this.getConfigValue('listDisplayContextVariable', '');

    if (!contextVariable || !this.viewManager?.contextData) {
      this.listItems = [];
      return;
    }

    const contextData = this.viewManager.contextData[contextVariable];

    this.listItems = toContextArrayOrEmpty(contextData);
  }

  // Configuration getters
  get label(): string {
    return this.getConfigValue('listDisplayLabel', '');
  }

  get labelFontSize(): number {
    return this.getConfigValue('listDisplayLabelFontSize', 14);
  }

  get labelFontWeight(): string {
    return this.getConfigValue('listDisplayLabelFontWeight', 'bold');
  }

  get labelFontStyle(): string {
    return this.getConfigValue('listDisplayLabelFontStyle', 'normal');
  }

  get labelColor(): string {
    return this.getConfigValue('listDisplayLabelColor', '#000000');
  }

  get rowElements(): ListDisplayRowElement[] {
    return this.getConfigValue('listDisplayRowElements', []);
  }

  get rowFontSize(): number {
    return this.getConfigValue('listDisplayRowFontSize', 14);
  }

  get rowFontWeight(): string {
    return this.getConfigValue('listDisplayRowFontWeight', 'normal');
  }

  get rowFontStyle(): string {
    return this.getConfigValue('listDisplayRowFontStyle', 'normal');
  }

  get rowColor(): string {
    return this.getConfigValue('listDisplayRowColor', '#000000');
  }

  get rowSpacing(): number {
    return this.getConfigValue('listDisplayRowSpacing', 4);
  }

  get emptyMessage(): string {
    return this.getConfigValue('listDisplayEmptyMessage', 'No items to display');
  }

  // Render a single item using the row template
  renderItem(item: any): string {
    if (!item || !this.rowElements.length) {
      return '';
    }

    return this.rowElements.map(element => {
      if (element.type === 'text') {
        return element.textValue || '';
      } else if (element.type === 'field') {
        if (!element.fieldName) {
          return '';
        }
        const value = item[element.fieldName];
        return this.formatValue(value, element.fieldFormat);
      }
      return '';
    }).join('');
  }

  private formatValue(value: any, format?: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (!format) {
      return String(value);
    }

    switch (format) {
      case 'currency':
        const numValue = Number(value);
        if (isNaN(numValue)) return String(value);
        return numValue.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        });

      case 'number':
        const num = Number(value);
        if (isNaN(num)) return String(value);
        return num.toLocaleString();

      case 'percent':
        const pct = Number(value);
        if (isNaN(pct)) return String(value);
        return `${(pct * 100).toFixed(1)}%`;

      case 'date':
        const date = new Date(value);
        if (isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString();

      default:
        return String(value);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}
